// components/ImageEditor/ImageEditorModal.tsx
import React, { useState, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { useAppStore } from "@/core/store/useAppStore";
import { useSimpleMediaLibrary } from "@/core/MediaLibrary/hooks/useSimpleMediaLibrary";
import { DualFormatEditor } from "./DualFormatEditor";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { UnifiedAuthenticatedImage } from "@/components/ui/UnifiedAuthenticatedImage";

export const ImageEditorModal: React.FC = () => {
  const { ui, setActiveModal, selectedClient } = useAppStore();
  const [selectedImage, setSelectedImage] = useState<MediaItem | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const isOpen = ui.activeModal?.name === "image-editor";

  const mediaLibrary = useSimpleMediaLibrary({
    clientId: selectedClient?.id || '',
    autoLoad: false,
    contentTypes: ["photos"]
  });

  const images = mediaLibrary.images;
  const isLoading = mediaLibrary.isLoading;
  const error = mediaLibrary.error;
  const isStreamingComplete = !mediaLibrary.isLoading && mediaLibrary.hasData;

  // Load images only when modal opens
  useEffect(() => {
    if (isOpen && selectedClient?.id) {
      mediaLibrary.loadFromDatabase();
    }
  }, [isOpen, selectedClient?.id]);

  // Filter images based on search query
  const filteredImages = images.filter((image: MediaItem) =>
    image.originalFilename.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Reset selected image when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedImage(null);
      setSearchQuery("");
    }
  }, [isOpen]);

  const handleClose = () => {
    setActiveModal(null);
    setSelectedImage(null);
    setSearchQuery("");
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Image Editor - Dual Format (Post 4:5 + Reels 9:16)"
      size="xl"
      className="max-w-7xl"
    >
      <div className="flex flex-col h-[85vh]">
        {!selectedImage ? (
          // Image Selection View
          <div className="flex-1 p-6">
            {/* Header */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select an image to edit
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Optimize your images to work perfectly in both Instagram post
                format (4:5) and reels cover format (9:16)
              </p>
            </div>

            {/* No Client Selected */}
            {!selectedClient && (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <div className="text-center">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6m-6 4a2 2 0 100 4 2 2 0 000-4z"
                    />
                  </svg>
                  <p className="text-lg font-medium">No client selected</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Select a client to access their image library
                  </p>
                </div>
              </div>
            )}

            {/* Loading State */}
            {selectedClient && isLoading && images.length === 0 && (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading images...</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Please wait while we fetch the image library
                  </p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && images.length === 0 && (
              <div className="flex items-center justify-center py-16 text-red-600">
                <div className="text-center">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-red-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <p className="text-lg font-medium">Failed to load images</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* No Images Found */}
            {selectedClient &&
              isStreamingComplete &&
              images.length === 0 &&
              !error && (
                <div className="flex items-center justify-center py-16 text-gray-500">
                  <div className="text-center">
                    <svg
                      className="w-16 h-16 mx-auto mb-4 text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-lg font-medium">No images found</p>
                    <p className="text-sm text-gray-400 mt-1">
                      This client doesn't have any images in their library
                    </p>
                  </div>
                </div>
              )}

            {/* No Search Results */}
            {images.length > 0 &&
              filteredImages.length === 0 &&
              searchQuery && (
                <div className="flex items-center justify-center py-16 text-gray-500">
                  <div className="text-center">
                    <svg
                      className="w-16 h-16 mx-auto mb-4 text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <p className="text-lg font-medium">No matching images</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Try adjusting your search terms
                    </p>
                  </div>
                </div>
              )}

            {/* Image Grid */}
            {filteredImages.length > 0 && (
              <div>
                {/* Results Info */}
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-gray-600">
                    {searchQuery ? (
                      <>
                        Showing {filteredImages.length} of {images.length}{" "}
                        images
                        {isLoading && !isStreamingComplete && (
                          <span className="text-blue-600 ml-2">
                            (loading more...)
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        {images.length} image{images.length !== 1 ? "s" : ""}{" "}
                        available
                        {isLoading && !isStreamingComplete && (
                          <span className="text-blue-600 ml-2">
                            (loading more...)
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSearchQuery("")}
                    >
                      Clear search
                    </Button>
                  )}
                </div>

                {/* Grid */}
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                  {filteredImages.map((image: MediaItem) => (
                    <div
                      key={image.id}
                      onClick={() => setSelectedImage(image)}
                      className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                    >
                      <UnifiedAuthenticatedImage
                        mediaItem={image}
                        alt={image.originalFilename}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        useThumbnail={true}
                      />

                      {/* Image overlay info */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors">
                        <div className="absolute bottom-0 left-0 right-0 p-2 bg-linear-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-white text-xs font-medium truncate">
                            {image.originalFilename}
                          </p>
                          {image.group && (
                            <p className="text-white/80 text-xs truncate">
                              {image.group}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Group indicator */}
                      {image.group && (
                        <div
                          className="absolute top-2 right-2 w-3 h-3 rounded-full border border-white shadow-xs"
                          style={{
                            backgroundColor: `hsl(${(image.group.charCodeAt(0) * 137.508) % 360}, 70%, 75%)`,
                          }}
                          title={image.group}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Image Editor View
          <DualFormatEditor
            image={selectedImage}
            onBack={() => setSelectedImage(null)}
            onClose={handleClose}
          />
        )}

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-between">
          <Button variant="ghost" onClick={handleClose}>
            Close
          </Button>

          {selectedImage && (
            <Button variant="ghost" onClick={() => setSelectedImage(null)}>
              ← Back to Gallery
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
